import BaseModel from './BaseModel';
import Change from './Change';
import Client from './Client';
import Project from './Project';
import _ from 'lodash';
import moment from 'moment';
import type { Moment } from 'moment';
import { formatPrice } from '../locales';

export type QuoteBillingPeriod = 'oneTime' | 'monthly' | 'quarterly' | 'annual';

export const QUOTE_BILLING_PERIODS: QuoteBillingPeriod[] = [
    'oneTime',
    'monthly',
    'quarterly',
    'annual',
];

export const QUOTE_RECURRING_PERIODS: QuoteBillingPeriod[] = [
    'monthly',
    'quarterly',
    'annual',
];

export type QuoteOptionMode = 'fixed' | 'included' | 'excluded';

export type QuoteItem = {
    _id?: string;
    title: string;
    description: string;
    type: string;
    estimate: number;
    price: number;
    billingPeriod?: QuoteBillingPeriod;
    free: boolean;
    removable?: boolean;
    /** fixed (required) | included (opt-out) | excluded (opt-in). Syncs with removable. */
    optionMode?: QuoteOptionMode;
    quantityMode?: boolean;
    quantity?: number;
    quantityMin?: number;
    quantityMax?: number;
    excluded?: boolean;
    change?: Change;
    zoneClientItemId?: number | null;
    zoneClientItemName?: string | null;
    zoneClientItemposId?: number | null;
    zoneClientExternalRef?: string | null;
    zoneClientSyncedAt?: string | Moment | null;
    zoneClientSyncError?: string | null;
}

export function quoteItemOptionMode(item?: QuoteItem | null): QuoteOptionMode {
    const mode = item?.optionMode;
    if (mode === 'fixed' || mode === 'included' || mode === 'excluded')
        return mode;
    if (item?.removable)
        return 'included';
    return 'fixed';
}

export function quoteItemIsOptional(item?: QuoteItem | null): boolean {
    if (item?.quantityMode)
        return false;
    return quoteItemOptionMode(item) !== 'fixed';
}

export function quoteItemBillingPeriod(item: QuoteItem): QuoteBillingPeriod {
    if (item?.billingPeriod && QUOTE_BILLING_PERIODS.includes(item.billingPeriod))
        return item.billingPeriod;
    return 'oneTime';
}

export function quoteItemIsAnnual(item: QuoteItem): boolean {
    return quoteItemBillingPeriod(item) === 'annual';
}

export function quoteItemIsRecurring(item: QuoteItem): boolean {
    return QUOTE_RECURRING_PERIODS.includes(quoteItemBillingPeriod(item));
}

export function quoteItemPriceSuffix(item: QuoteItem): string {
    switch (quoteItemBillingPeriod(item)) {
        case 'monthly': return '/ Mois';
        case 'quarterly': return '/ Trim';
        case 'annual': return '/ An';
        default: return '';
    }
}

/** Line contribution to quote total (unit × qty when quantityMode). */
export function quoteItemLineAmount(item: QuoteItem): number {
    if (!item || item.free || item.excluded)
        return 0;
    const price = Number(item.price) || 0;
    if (!item.quantityMode)
        return price;
    const qty = Number(item.quantity);
    if (!Number.isFinite(qty) || qty <= 0)
        return 0;
    return price * Math.floor(qty);
}

export function quotePeriodTotal(items: QuoteItem[], period: QuoteBillingPeriod): number {
    return (items || []).reduce((acc, item) => {
        if (quoteItemBillingPeriod(item) !== period)
            return acc;
        return acc + quoteItemLineAmount(item);
    }, 0);
}

export function quoteOneTimeTotal(items: QuoteItem[]): number {
    return quotePeriodTotal(items, 'oneTime');
}

export function quoteMonthlyTotal(items: QuoteItem[]): number {
    return quotePeriodTotal(items, 'monthly');
}

export function quoteQuarterlyTotal(items: QuoteItem[]): number {
    return quotePeriodTotal(items, 'quarterly');
}

export function quoteAnnualTotal(items: QuoteItem[]): number {
    return quotePeriodTotal(items, 'annual');
}

export function quoteHasRecurringFees(items: QuoteItem[]): boolean {
    return (items || []).some((item) => quoteItemIsRecurring(item) && ((item.price || 0) !== 0 || item.free));
}

export function quoteItemQuantityEditable(item: QuoteItem): boolean {
    if (!item?.quantityMode)
        return false;
    const min = Number(item.quantityMin);
    const max = Number(item.quantityMax);
    return Number.isFinite(min) && Number.isFinite(max) && min < max;
}

export type QuoteSend = {
    sentAt?: Moment;
    to?: string[];
    cc?: string[];
    subject?: string;
    messageId?: string;
    personalMessage?: string;
}

export type QuoteSignature = {
    signedAt?: Moment;
    signerName?: string;
    signerRole?: string;
    signerCompany?: string;
    signatureImage?: string;
    signatureSource?: 'draw' | 'upload' | null;
    contentHash?: string;
    acceptedItemIds?: string[];
    acceptedQuantities?: Record<string, number> | null;
}

export type QuoteAction = {
    type?: string;
    date?: Moment;
}

/** Class representing an user. */
class Quote extends BaseModel {
    /**
     * Status constants
     */
    static STATUS_PENDING        = 'pending';
    static STATUS_SUBMITTED      = 'submitted';
    static STATUS_REFUSED        = 'refused';
    static STATUS_ACCEPTED       = 'accepted';
    static STATUS_ADVANCE_BILLED = 'advanceBilled';
    static STATUS_ADVANCE_PAID   = 'advancePaid';
    static STATUS_BILLED         = 'billed';
    static STATUS_PAID           = 'paid';

    _id: string;
    name: string;
    slug: string;
    project: Project;
    client: Client;
    /** Card where send / client replies are mirrored. */
    trackedChange?: Change | null;
    items: QuoteItem[];
    price: number;
    monthlyPrice: number;
    quarterlyPrice: number;
    annualPrice: number;
    needsAdvance: boolean;
    status: string;
    createdAt?: Moment;
    submittedAt?: Moment;
    acceptedAt?: Moment;
    refusedAt?: Moment;
    advanceBilledAt?: Moment;
    advancePaidAt?: Moment;
    billedAt?: Moment;
    paidAt?: Moment;
    firstViewedAt?: Moment;
    lastViewedAt?: Moment;
    viewCount?: number;
    partialAcceptance?: boolean;
    introBody?: string;
    presentationBody?: string;
    conditionsBody?: string;
    publicToken?: string | null;
    sends?: QuoteSend[];
    signature?: QuoteSignature;

    constructor(properties: AnyObj) {
        super({});

        this._id          = properties._id;
        this.name         = properties.name;
        this.slug         = properties.slug;
        this.status       = properties.status;
        this.price          = properties.price;
        this.monthlyPrice   = properties.monthlyPrice || 0;
        this.quarterlyPrice = properties.quarterlyPrice || 0;
        this.annualPrice    = properties.annualPrice || 0;
        this.needsAdvance   = !!properties.needsAdvance;
        this.introBody    = properties.introBody || '';
        this.presentationBody = properties.presentationBody || '';
        this.conditionsBody = properties.conditionsBody || '';
        this.createdAt    = properties.createdAt ? moment(properties.createdAt) : undefined;

        this.project = properties.project;
        if (_.isObject(this.project))
            this.project = new Project(this.project);

        this.client = properties.client;
        if (_.isObject(this.client))
            this.client = new Client(this.client);

        this.trackedChange = properties.trackedChange || null;
        if (_.isObject(this.trackedChange) && this.trackedChange._id)
            this.trackedChange = new Change(this.trackedChange);

        this.items = (properties.items || []).map((s: QuoteItem) => {
            const optionMode = quoteItemOptionMode(s);
            return {
                ...s,
                optionMode,
                removable: optionMode !== 'fixed' && !s.quantityMode,
                change: s.change && s.change._id ? new Change(s.change) : s.change,
            };
        });

        if (properties.submittedAt)
            this.submittedAt = moment(properties.submittedAt);
        if (properties.acceptedAt)
            this.acceptedAt = moment(properties.acceptedAt);
        if (properties.refusedAt)
            this.refusedAt = moment(properties.refusedAt);
        if (properties.advanceBilledAt)
            this.advanceBilledAt = moment(properties.advanceBilledAt);
        if (properties.advancePaidAt)
            this.advancePaidAt = moment(properties.advancePaidAt);
        if (properties.billedAt)
            this.billedAt = moment(properties.billedAt);
        if (properties.paidAt)
            this.paidAt = moment(properties.paidAt);
        if (properties.firstViewedAt)
            this.firstViewedAt = moment(properties.firstViewedAt);
        if (properties.lastViewedAt)
            this.lastViewedAt = moment(properties.lastViewedAt);
        this.viewCount = properties.viewCount || 0;
        this.partialAcceptance = !!properties.partialAcceptance;
        this.publicToken = properties.publicToken || null;
        this.sends = (properties.sends || []).map((send: AnyObj) => ({
            ...send,
            sentAt: send.sentAt ? moment(send.sentAt) : undefined,
        }));
        if (properties.signature) {
            this.signature = {
                ...properties.signature,
                signedAt: properties.signature.signedAt
                    ? moment(properties.signature.signedAt)
                    : undefined,
            };
        }
    }

    defaultSendSubject(reminder = false) {
        const base = `Devis ${this.slug} — ${this.name}`;
        return reminder ? `Relance — ${base}` : base;
    }

    isOpenForClientDecision() {
        return this.status === Quote.STATUS_PENDING || this.status === Quote.STATUS_SUBMITTED;
    }

    hasBeenSent() {
        return (this.sends || []).length > 0 || this.status !== Quote.STATUS_PENDING;
    }

    /** Absolute URL of the client-facing quote page, or null if no public token yet. */
    publicUrl() {
        if (!this.publicToken)
            return null;
        if (typeof window === 'undefined' || !window.location?.origin)
            return `/public/quotes/${this.publicToken}`;
        return `${window.location.origin}/public/quotes/${this.publicToken}`;
    }

    static allStatus() : string[] {
        return [
            Quote.STATUS_PENDING,
            Quote.STATUS_SUBMITTED,
            Quote.STATUS_REFUSED,
            Quote.STATUS_ACCEPTED,
            Quote.STATUS_ADVANCE_BILLED,
            Quote.STATUS_ADVANCE_PAID,
            Quote.STATUS_BILLED,
            Quote.STATUS_PAID
        ];
    }

    statusIndex(status?: string) {
        return Quote.allStatus().indexOf(status || this.status);
    }

    statusColor() {
        const index = this.statusIndex();
        return [
            'yellow',
            'blue',
            'red',
            'green',
            'blue',
            'blue',
            'blue',
            'blue',
        ][index];
    }

    totalEstimated() {
        return (this.items || []).reduce((acc: number, item: QuoteItem) => {
            return acc + item.estimate;
        }, 0);
    }

    progression() : number {
        if (this.statusIndex() < this.statusIndex(Quote.STATUS_ACCEPTED))
            return 0;


        const totalRealised = this.items.reduce((acc: number, item: QuoteItem) => {
            if (!item.change)
                return acc + item.estimate;

            return acc + (
                item.change.estimate * item.change.progression() / 100
            );

        }, 0);

        return totalRealised / this.totalEstimated() * 100;
    }

    lastAction() : QuoteAction {
        const steps = [
            'createdAt',
            'submittedAt',
            'acceptedAt',
            'refusedAt',
            'advanceBilledAt',
            'advancePaidAt',
            'billedAt',
            'paidAt'
        ];

        const action : QuoteAction = {};
        steps.forEach((property: string) => {
            if (this[property as keyof typeof this]) {
                action.type = property;
                action.date = this[property as keyof typeof this] as Moment;
            }
        });

        return action;
    }

    isFinished() {
        return (
            this.status !== Quote.STATUS_REFUSED &&
            this.status !== Quote.STATUS_PAID
        );
    }

    formattedPrice() {
        return formatPrice(this.price || 0);
    }

    formattedMonthlyPrice() {
        return formatPrice(this.monthlyPrice || 0);
    }

    formattedQuarterlyPrice() {
        return formatPrice(this.quarterlyPrice || 0);
    }

    formattedAnnualPrice() {
        return formatPrice(this.annualPrice || 0);
    }

    hasRecurringFees() {
        return quoteHasRecurringFees(this.items)
            || (Number(this.monthlyPrice) || 0) > 0
            || (Number(this.quarterlyPrice) || 0) > 0
            || (Number(this.annualPrice) || 0) > 0;
    }

    /** @deprecated use hasRecurringFees */
    hasAnnualFees() {
        return this.hasRecurringFees();
    }
}

export default Quote;
